import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import {
    useReactTable,
    ColumnFiltersState,
    getCoreRowModel,
    getFilteredRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFacetedMinMaxValues,
    getPaginationRowModel,
    getSortedRowModel,
    FilterFn,
    ColumnDef,
    flexRender,
    getExpandedRowModel,
    Table,
    ColumnFilter
} from '@tanstack/react-table';
import { ICoreContext } from '@msdyn365-commerce/core';
import {
    formatDate,
    formatCurrency,
    formatNumber,
    mapOverallStatus,
    mapItemStatusCodeToText,
    formatOverallStatusCodeForTooltip,
    formatStatusCodeForTooltip
} from '../../../utilities';
import { getOrderDetailsPageUrl } from '../../../utilities/get-order-details-page-url';
import {
    MULTI_VALUE_SEPARATOR,
    NO_DATA_FALLBACK,
    ORDER_HISTORY_FILTER_CREATED_ON,
    ORDER_HISTORY_FILTER_FROM_DATE,
    ORDER_HISTORY_FILTER_FULLNAME,
    ORDER_HISTORY_FILTER_STATUS,
    ORDER_HISTORY_FILTER_TO_DATE,
    ORDER_HISTORY_COLUMN_ORDER_NUMBER,
    ORDER_HISTORY_COLUMN_ACTION,
    ORDER_HISTORY_COLUMN_PO_NUMBER,
    ORDER_HISTORY_COLUMN_SOLD_TO_NAME,
    ORDER_HISTORY_COLUMN_SHIP_TO_NAME,
    ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE,
    ORDER_DOCUMENTS_SECTION,
    OrderStatus
} from '../../../configuration';
import Tooltip from '../../../components/tooltip';
import SearchBox from './search-box';
import { IOrderHistoryResources } from '../order-history.props.autogenerated';
import { IOrderHistoryTableData, IOrderHistorySchedulLine, IOrderHistoryLine } from '../../../actions/get-order-history.action';

export default function CreateOrderTable({
    resources,
    context,
    globalFilter,
    setGlobalFilter,
    orderHistoryTableData,
    pageSize,
    soldToIdsData
}: {
    resources: IOrderHistoryResources;
    context: ICoreContext;
    globalFilter: string;
    setGlobalFilter: Dispatch<SetStateAction<string>>;
    orderHistoryTableData: IOrderHistoryTableData[] | undefined;
    pageSize: number;
    props?: IOrderHistoryTableData;
    soldToIdsData: string[];
}) {
    /*
     * Sample filter function for multiple values.
     * Use for later reference.
     */
    const multiSelectFilter: FilterFn<any> = (row, columnId, value) => {
        const currentValue = mapOverallStatus(resources).getText(row.getValue(columnId));
        const selectedValues = value.split(MULTI_VALUE_SEPARATOR);
        return selectedValues.includes(currentValue);
    };

    /**
     * Filters a table row based on the nested structure of order lines and schedule lines,
     * checking if any schedule line's `ConfirmedDate` falls within the specified date range.
     *
     * @param row - Array of order lines.
     * @param value - Array with `from` and `to` date range.
     * @returns {boolean} - Returns `true` if any `ConfirmedDate` falls within the specified date range.
     */
    const nestedDateRangeFilter: FilterFn<any> = (row, columnId, value) => {
        const [from, to] = value;
        const orderLines: IOrderHistoryLine[] = row.original.OrderLines || [];
        return orderLines.some((orderLine: IOrderHistoryLine) => {
            const scheduleLines = orderLine.ScheduleLines;
            return scheduleLines.some((scheduleLine: IOrderHistorySchedulLine) => {
                const currentValue = scheduleLine.ConfirmedDate;
                if (
                    currentValue &&
                    (!from || new Date(from).getTime() <= new Date(currentValue).getTime()) &&
                    (!to || new Date(currentValue).getTime() <= new Date(to).getTime())
                ) {
                    setExpanded(old => ({ ...old, [row.id]: !old[row.id] }));
                    return true;
                } else {
                    return false;
                }
            });
        });
    };

    /*
     * Sample filter function for date ranges.
     * Use for later reference.
     */
    const dateRangeFilter: FilterFn<any> = (row, columnId, value) => {
        const currentValue = row.getValue<string>(columnId);
        const [from, to] = value;
        return (
            (!from || new Date(from).getTime() <= new Date(currentValue.split('T')[0]).getTime()) &&
            (!to || new Date(currentValue.split('T')[0]).getTime() <= new Date(to).getTime())
        );
    };

    const [expanded, setExpanded] = useState({});

    const [searchTerm, setSearchTerm] = useState<boolean>(false);

    const [pageIndex, setPageIndex] = useState(0);

    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

    const columns = useMemo<ColumnDef<IOrderHistoryTableData, any>[]>(
        () => [
            {
                accessorFn: row => row.OrderNumber,
                id: ORDER_HISTORY_COLUMN_ORDER_NUMBER,
                header: resources.orderHistoryOrderNumberText,
                cell: info => {
                    if (info.row.original.Status === OrderStatus.Queued) {
                        return flexRender(highlightSearchTerm(String(info.getValue()), globalFilter), info);
                    }
                    return flexRender(
                        <a href={`${getOrderDetailsPageUrl(context.actionContext, Number(info.row.original.OrderNumber))}`}>
                            {highlightSearchTerm(String(info.getValue()), globalFilter)}
                        </a>,
                        info
                    );
                },
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.PoNumber,
                id: ORDER_HISTORY_COLUMN_PO_NUMBER,
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter),
                header: () => resources.orderHistorySalesOrderPONumber,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.ConfirmedDate,
                id: ORDER_HISTORY_FILTER_CREATED_ON,
                header: resources.orderHistorySalesOrderCreatedOn,
                cell: info => formatDate(context, new Date(info.getValue())),
                footer: props => props.column.id,
                filterFn: dateRangeFilter
            },
            {
                accessorFn: row => row.Status,
                id: ORDER_HISTORY_FILTER_STATUS,
                cell: info => highlightSearchTerm(mapOverallStatus(resources).getText(String(info.getValue())), globalFilter),
                header: () => (
                    <Tooltip content={formatOverallStatusCodeForTooltip(resources)} label={resources.orderHistorySalesOrderStatus} />
                ),
                footer: props => props.column.id,
                filterFn: multiSelectFilter
            },
            {
                accessorFn: row => row.SoldToName,
                id: ORDER_HISTORY_COLUMN_SOLD_TO_NAME,
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter),
                header: () => resources.orderHistorySalesOrderSoldTo,
                footer: props => props.column.id
            },
            {
                accessorFn: row => row.ShipToName,
                id: ORDER_HISTORY_COLUMN_SHIP_TO_NAME,
                cell: info => highlightSearchTerm(String(info.getValue()), globalFilter),
                header: () => resources.orderHistorySalesOrderShipTo,
                footer: props => props.column.id
            },
            {
                id: ORDER_HISTORY_COLUMN_ACTION,
                cell: info => info.getValue() ?? NO_DATA_FALLBACK,
                header: () => resources.orderHistorySalesOrderAction,
                footer: props => props.column.id,
                enableSorting: false
            },
            {
                id: ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE,
                filterFn: nestedDateRangeFilter,
                enableHiding: true //utilize react-table filter functionality for nested column values while keeping original table header.
            }
        ],
        [globalFilter]
    );

    const highlightSearchTerm = (text: string, searchTerm: string): JSX.Element => {
        if (!searchTerm.trim()) {
            return <span>{text}</span>;
        }
        const parts = text?.split(new RegExp(`(${searchTerm})`, 'gi'));

        return (
            <span>
                {parts.map((part, index) =>
                    part.toLowerCase() === searchTerm.toLowerCase() ? (
                        <span key={index} className='ms-order-history__highlight-search-term'>
                            {part}
                        </span>
                    ) : (
                        part
                    )
                )}
            </span>
        );
    };

    // Filter function to search for all order items based on user input
    const customGlobalFilter: FilterFn<IOrderHistoryTableData> = (row, _, filterValue) => {
        let highlightRow = false;

        filterValue !== '' && setSearchTerm(true);

        // Check for top-level properties
        const inMainData = Object.entries(row.original).some(([key, value]) => {
            if (typeof value === 'string') {
                if (key === 'Status') {
                    const mappedValue = mapOverallStatus(resources).getText(value);
                    return mappedValue.toLowerCase().includes(filterValue.toLowerCase());
                } else {
                    return value.toLowerCase().includes(filterValue.toLowerCase());
                }
            }
            return false;
        });

        if (inMainData) {
            highlightRow = true;
        }

        // Check for OrderLines inside the top-level items
        const inOrderLines = row.original.OrderLines.some(orderLine => {
            return Object.entries(orderLine).some(([key, value]) => {
                const mappedValue = typeof value === 'string' && key === 'Status' ? mapItemStatusCodeToText(value, resources) : value;
                const match = mappedValue
                    .toString()
                    .toLowerCase()
                    .includes(filterValue.toLowerCase());
                if (match) {
                    highlightRow = true;
                }
                return match;
            });
        });

        row.original.highlight = highlightRow;
        row.original.highlightOrderLines = inOrderLines;

        return highlightRow || inOrderLines;
    };

    const [data, setData] = useState<IOrderHistoryTableData[]>([]);

    useEffect(() => {
        if (orderHistoryTableData !== undefined) {
            setData(orderHistoryTableData);
        }
    }, [orderHistoryTableData, data]);

    const defaultSorting = useMemo(
        () => [
            {
                id: ORDER_HISTORY_FILTER_CREATED_ON,
                desc: true
            },
            {
                id: ORDER_HISTORY_COLUMN_ORDER_NUMBER,
                desc: true
            }
        ],
        []
    );

    const table = useReactTable({
        data,
        columns,
        filterFns: {},
        initialState: { sorting: defaultSorting, columnVisibility: { confirmedDate: false } },
        state: {
            expanded,
            columnFilters,
            globalFilter,
            pagination: {
                pageIndex: pageIndex,
                pageSize: pageSize
            }
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: customGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        debugTable: false,
        debugHeaders: false,
        debugColumns: false
    });

    const totalEntries = table.getFilteredRowModel().rows.length;

    const addUrlDateParameter = (paramName: string, paramValue: string) => {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set(paramName, paramValue);
        window.history.pushState({}, '', (currentUrl as unknown) as string);
    };

    const removeUrlParameter = (paramName: string) => {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete(paramName);
        window.history.pushState({}, '', (currentUrl as unknown) as string);
        setExpanded({});
    };

    const addUrlStatusParameter = (values: string[]) => {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete(ORDER_HISTORY_FILTER_STATUS);
        values.forEach(val => currentUrl.searchParams.append(ORDER_HISTORY_FILTER_STATUS, val));
        window.history.pushState({}, '', (currentUrl as unknown) as string);
    };

    useEffect(() => {
        if (table.getState().columnFilters[0]?.id === ORDER_HISTORY_FILTER_FULLNAME) {
            if (table.getState().sorting[0]?.id !== ORDER_HISTORY_FILTER_FULLNAME) {
                table.setSorting([{ id: ORDER_HISTORY_FILTER_FULLNAME, desc: false }]);
            }
        }
    }, [table.getState().columnFilters[0]?.id]);

    useEffect(() => {
        const url = new URL(window.location.href);
        const fromDate = url.searchParams.get(ORDER_HISTORY_FILTER_FROM_DATE) ?? '';
        const toDate = url.searchParams.get(ORDER_HISTORY_FILTER_TO_DATE) ?? '';
        const filters = [];
        if (fromDate || toDate) {
            filters.push({ id: ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE, value: [fromDate, toDate] });
        }
        const status = url.searchParams.getAll(ORDER_HISTORY_FILTER_STATUS);

        /**
         * Filter out invalid status codes, to prevent adding them to the filter state.
         */
        const validStatus = status.filter(code => mapOverallStatus(resources).getCodeExists(code));

        if (validStatus.length > 0) {
            filters.push({
                id: ORDER_HISTORY_FILTER_STATUS,
                value: validStatus
                    .map(code => {
                        return mapOverallStatus(resources).getText(code);
                    })
                    .join(MULTI_VALUE_SEPARATOR)
            });
        }

        /**
         * If the URL contains invalid status codes, update the URL to remove them.
         */
        if (validStatus.length !== status.length) {
            addUrlStatusParameter(validStatus);
        }

        setColumnFilters(filters);
    }, []);

    useEffect(() => {
        if (searchTerm && !globalFilter) {
            setSearchTerm(false);
            setExpanded({});
        }

        setPageIndex(0);
    }, [globalFilter, columnFilters]);

    const ActiveFiltersDisplay = ({ columnFilters }: { columnFilters: ColumnFiltersState }) => {
        return (
            <div className='ms-order-history__active-filters'>
                <h5 className='ms-order-history__active-filters-heading'>{resources.orderHistoryActiveFilterHeading}</h5>

                {columnFilters.map((filter: ColumnFilter, index: number) => (
                    <div className='ms-order-history__active-filter' key={index}>
                        {filter.id === ORDER_HISTORY_FILTER_STATUS ? (
                            <>
                                <span className='ms-order-history__active-filter-label'>{resources.orderHistoryFilterStatusLegend}</span>
                                <div className='ms-order-history__active-filter-container'>
                                    {(filter.value as string).split(MULTI_VALUE_SEPARATOR).map((value: string) => (
                                        <div className='ms-order-history__active-filter-item' key={value}>
                                            {value}
                                            <button
                                                onClick={() => {
                                                    const newFilters = columnFilters.find(
                                                        (item: ColumnFilter) => item.id === ORDER_HISTORY_FILTER_STATUS
                                                    );
                                                    if (newFilters) {
                                                        const separatedFilter = (newFilters.value as string).split(MULTI_VALUE_SEPARATOR);
                                                        separatedFilter.splice(
                                                            separatedFilter.findIndex(i => i === value),
                                                            1
                                                        );
                                                        const key = mapOverallStatus(resources).getCode(value);
                                                        const currentUrl = new URL(window.location.href);
                                                        const status = currentUrl.searchParams.getAll(ORDER_HISTORY_FILTER_STATUS);
                                                        addUrlStatusParameter(status.filter((val: string) => val !== key));
                                                        const concatenatedFilter = separatedFilter.join(MULTI_VALUE_SEPARATOR);
                                                        if (concatenatedFilter) {
                                                            setColumnFilters([
                                                                ...columnFilters.filter(fil => fil.id !== ORDER_HISTORY_FILTER_STATUS),
                                                                { id: ORDER_HISTORY_FILTER_STATUS, value: concatenatedFilter }
                                                            ]);
                                                        } else {
                                                            setColumnFilters(
                                                                columnFilters.filter(fil => fil.id !== ORDER_HISTORY_FILTER_STATUS)
                                                            );
                                                        }
                                                    }
                                                }}
                                                className='ms-order-history__active-filter-item-remove-button'
                                            ></button>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : filter.id === ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE ? (
                            (filter.value as string[]).map((date, index) => {
                                if (date === '') {
                                    return null;
                                }
                                return (
                                    <>
                                        <span className='ms-order-history__active-filter-label'>
                                            {index === 0 ? resources.orderHistoryFilterFrom : resources.orderHistoryFilterTo}
                                        </span>
                                        <div className='ms-order-history__active-filter-container'>
                                            <div className='ms-order-history__active-filter-item' key={date}>
                                                {formatDate(context, new Date(date))}
                                                <button
                                                    onClick={() => {
                                                        const newFilters = columnFilters.find(
                                                            (item: ColumnFilter) => item.id === ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE
                                                        );
                                                        if (newFilters) {
                                                            (newFilters.value as string[]).splice(index, 1, '');
                                                            if ((newFilters.value as string[])[0] === '') {
                                                                removeUrlParameter(ORDER_HISTORY_FILTER_FROM_DATE);
                                                            }
                                                            if ((newFilters.value as string[])[1] === '') {
                                                                removeUrlParameter(ORDER_HISTORY_FILTER_TO_DATE);
                                                            }
                                                            if (newFilters && (newFilters.value as string[]).some(val => val !== '')) {
                                                                setColumnFilters([
                                                                    ...columnFilters.filter(
                                                                        fil => fil.id !== ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE
                                                                    ),
                                                                    {
                                                                        id: ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE,
                                                                        value: newFilters.value
                                                                    }
                                                                ]);
                                                            } else {
                                                                setColumnFilters(
                                                                    columnFilters.filter(
                                                                        fil => fil.id !== ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE
                                                                    )
                                                                );
                                                            }
                                                        }
                                                    }}
                                                    className='ms-order-history__active-filter-item-remove-button'
                                                ></button>
                                            </div>
                                        </div>
                                    </>
                                );
                            })
                        ) : (
                            <>
                                <span className='ms-order-history__active-filter-label'>{filter.id}</span>
                                <div className='ms-order-history__active-filter-container'>
                                    <div className='ms-order-history__active-filter-item'>
                                        {filter.value}
                                        <button
                                            onClick={() => {
                                                setColumnFilters(columnFilters.filter((item: ColumnFilter) => item !== filter));
                                            }}
                                            className='ms-order-history__active-filter-item-remove-button'
                                        ></button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const ResetFilters = () => {
        setColumnFilters([]);
        removeUrlParameter(ORDER_HISTORY_FILTER_FROM_DATE);
        removeUrlParameter(ORDER_HISTORY_FILTER_TO_DATE);
        removeUrlParameter(ORDER_HISTORY_FILTER_STATUS);
    };
    /*
     * Allows to filter table column specific externally.
     * Use for later reference.
     */
    function FilterBox({ table }: { table: Table<any> }) {
        const scheduleLineConfirmedDate = table.getColumn(ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE);
        const dateValue = scheduleLineConfirmedDate?.getFilterValue();

        const [from, to] = (dateValue as string[]) || ['', ''];
        const statusColumn = table.getColumn(ORDER_HISTORY_FILTER_STATUS);
        const uniqueVals = statusColumn?.getFacetedUniqueValues();
        const uniqueKeys = Array.from(uniqueVals?.keys() || []).sort();
        const setMultiValue = (input: HTMLInputElement, column: any) => {
            const currentUrl = new URL(window.location.href);
            const status = currentUrl.searchParams.getAll(ORDER_HISTORY_FILTER_STATUS);
            const value = mapOverallStatus(resources).getText(input.value);
            let values = [...(column?.getFilterValue() ? column?.getFilterValue().split(MULTI_VALUE_SEPARATOR) : [])];
            if (input.checked && !values.includes(value)) {
                values.push(value);
                addUrlStatusParameter([...status, input.value]);
            } else {
                values = values.filter((val: string) => val !== value);
                addUrlStatusParameter(status.filter((val: string) => val !== input.value));
                setExpanded({});
            }
            column?.setFilterValue(values.sort().join(MULTI_VALUE_SEPARATOR));
        };
        const setDateValue = (column: any, from: string, to: string) => {
            column?.setFilterValue([from, to]);
        };

        const [selectedFromDate, setSelectedFromDate] = useState<string>(from);
        const [selectedToDate, setSelectedToDate] = useState<string>(to);

        return (
            <div className='msc-section-box'>
                <h4 className='ms-order-history__filter-title msc-section-box__title'>{resources.orderHistoryFilterHeading}</h4>

                <ActiveFiltersDisplay columnFilters={columnFilters} />
                <button onClick={ResetFilters} className='ms-order-history__filter-reset'>
                    {resources.orderHistoryResetFilter}
                </button>
                <div className='ms-order-history__filter-divider'></div>
                <legend>{resources.orderHistoryFilterConfirmedDateLegend}</legend>
                <div className='row'>
                    <fieldset className='msc-form__group col-sm col-lg-12'>
                        <label htmlFor={ORDER_HISTORY_FILTER_FROM_DATE}>{resources.orderHistoryFilterFromLabel}</label>
                        <input
                            id={ORDER_HISTORY_FILTER_FROM_DATE}
                            type='date'
                            value={selectedFromDate}
                            onChange={e => {
                                setSelectedFromDate(e.target.value);
                            }}
                            onBlur={() => {
                                setDateValue(scheduleLineConfirmedDate, selectedFromDate, to);
                                addUrlDateParameter(ORDER_HISTORY_FILTER_FROM_DATE, selectedFromDate);
                            }}
                            max={
                                (columnFilters.find(item => item.id === ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE)
                                    ?.value as string[])[1] ?? undefined
                            }
                        />
                    </fieldset>
                    <fieldset className='msc-form__group col-sm col-lg-12'>
                        <label htmlFor={ORDER_HISTORY_FILTER_TO_DATE}>{resources.orderHistoryFilterToLabel}</label>
                        <input
                            id={ORDER_HISTORY_FILTER_TO_DATE}
                            type='date'
                            value={selectedToDate}
                            min={
                                (columnFilters.find(item => item.id === ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE)
                                    ?.value as string[])[0] ?? undefined
                            }
                            onChange={e => setSelectedToDate(e.target.value)}
                            onBlur={() => {
                                setDateValue(scheduleLineConfirmedDate, from, selectedToDate);
                                addUrlDateParameter(ORDER_HISTORY_FILTER_TO_DATE, selectedToDate);
                            }}
                        />
                    </fieldset>
                </div>
                {uniqueKeys.length > 0 && (
                    <React.Fragment>
                        <div className='ms-order-history__filter-divider'></div>
                        <fieldset className='msc-form__group'>
                            <legend>{resources.orderHistoryFilterStatusLegend}</legend>
                            {uniqueKeys.map(key => (
                                <div className='msc-form__check' key={key}>
                                    <label htmlFor={key}>
                                        <input
                                            checked={(columnFilters.find(
                                                (filter: ColumnFilter) => filter.id === ORDER_HISTORY_FILTER_STATUS
                                            )?.value as string).includes(mapOverallStatus(resources).getText(key))}
                                            type='checkbox'
                                            name={mapOverallStatus(resources).getText(key)}
                                            value={key}
                                            id={key}
                                            onChange={e => setMultiValue(e.target, statusColumn)}
                                        />
                                        {mapOverallStatus(resources).getText(key)}
                                    </label>
                                </div>
                            ))}
                        </fieldset>
                    </React.Fragment>
                )}
            </div>
        );
    }

    return (
        <>
            <div className='row'>
                <div className='col-12 col-lg-9 order-2 order-lg-1'>
                    <h3 className='ms-order-history__total-entries'>
                        {totalEntries} {resources.orderHistoryTotalNumberOfOrders}
                    </h3>

                    <div className='table-responsive'>
                        <table className='table'>
                            <thead>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => {
                                            const sortValue =
                                                { asc: 'msc-table-sorting--asc', desc: 'msc-table-sorting--desc' }[
                                                    header.column.getIsSorted() as string
                                                ] ?? null;
                                            return (
                                                <th key={header.id} colSpan={header.colSpan}>
                                                    {header.isPlaceholder ? null : (
                                                        <>
                                                            <div
                                                                className={
                                                                    header.column.getCanSort()
                                                                        ? `msc-table-sorting ${sortValue ?? 'msc-table-sorting--default'} `
                                                                        : ''
                                                                }
                                                                onClick={header.column.getToggleSortingHandler()}
                                                            >
                                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                            </div>
                                                        </>
                                                    )}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {soldToIdsData?.length === 0 ? (
                                    <td colSpan={7} className='no-padding'>
                                        <div className='alert alert-warning' role='alert'>
                                            <span className='ms-order-history__alert-heading'>
                                                {resources.orderHistoryNoSoldTosMessageHeading}
                                            </span>
                                        </div>
                                        <p className='ms-order-history__alert-message'>{resources.orderHistoryNoSoldTosMessage}</p>
                                    </td>
                                ) : soldToIdsData?.length >= 1 &&
                                  orderHistoryTableData !== undefined &&
                                  orderHistoryTableData.length === 0 ? (
                                    <td colSpan={7} className='no-padding'>
                                        <div className='alert alert-warning' role='alert'>
                                            <span className='ms-order-history__alert-heading'>
                                                {resources.orderHistoryNoOrdersMessageHeading}
                                            </span>
                                        </div>
                                        <p className='ms-order-history__alert-message'>{resources.orderHistoryNoOrdersMessage}</p>
                                    </td>
                                ) : table.getRowModel().rows.length === 0 ? (
                                    <td colSpan={7} className='no-padding'>
                                        <div className='alert alert-info' role='alert'>
                                            <span className='ms-order-history__alert-heading'>
                                                {resources.orderHistoryEmptyTableMessageHeading}
                                            </span>
                                        </div>
                                        <p className='ms-order-history__alert-message'>{resources.orderHistoryEmptyTableMessage}</p>
                                    </td>
                                ) : (
                                    table.getRowModel().rows.map(row => (
                                        <React.Fragment key={row.id}>
                                            <tr>
                                                {row.getVisibleCells().map(cell => {
                                                    const highlightClass =
                                                        cell.column.id === ORDER_HISTORY_COLUMN_ACTION &&
                                                        row.original.highlightOrderLines &&
                                                        globalFilter !== ''
                                                            ? 'ms-order-history__highlight-search-term'
                                                            : '';
                                                    if (cell.column.id === ORDER_HISTORY_COLUMN_ACTION) {
                                                        return (
                                                            <td key={cell.id}>
                                                                <span className={highlightClass}>
                                                                    <a
                                                                        className='ms-order-history__table-collapse'
                                                                        data-open={row.getIsExpanded()}
                                                                        onClick={() =>
                                                                            cell.column.id === ORDER_HISTORY_COLUMN_ACTION &&
                                                                            setExpanded(old => ({ ...old, [row.id]: !old[row.id] }))
                                                                        }
                                                                    >
                                                                        {resources.orderHistoryDetailsFindLines}
                                                                    </a>
                                                                </span>
                                                            </td>
                                                        );
                                                    } else {
                                                        return (
                                                            <td key={cell.id}>
                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                            </td>
                                                        );
                                                    }
                                                })}
                                            </tr>
                                            {row.getIsExpanded() && (
                                                <tr>
                                                    <td colSpan={row.getVisibleCells().length}>
                                                        <div className='ms-order-history__table-orderlines'>
                                                            <table className='table table-sm'>
                                                                <thead>
                                                                    <tr>
                                                                        <th>{resources.orderHistoryDetailsLineNumber}</th>
                                                                        <th>{resources.orderHistoryDetailsProductName}</th>
                                                                        <th>
                                                                            <Tooltip
                                                                                label={resources.orderHistoryDetailsStatus}
                                                                                content={formatStatusCodeForTooltip(resources)}
                                                                            />
                                                                        </th>
                                                                        <th>{resources.orderHistoryDetailsRequestedDate}</th>
                                                                        <th>{resources.orderHistoryDetailsConfirmedDate}</th>
                                                                        <th>{resources.orderHistoryDetailsRequestedQuantity}</th>
                                                                        <th>{resources.orderHistoryDetailsConfirmedQuantity}</th>
                                                                        <th>
                                                                            <Tooltip
                                                                                label={resources.orderHistoryDetailsNetValue}
                                                                                content={resources.globalTableNetValueToolTip}
                                                                            />
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {row.original.OrderLines.map(
                                                                        (orderLine: IOrderHistoryLine, index: number) => {
                                                                            return (
                                                                                <React.Fragment key={index}>
                                                                                    <tr>
                                                                                        <td>
                                                                                            {highlightSearchTerm(
                                                                                                orderLine.LineNumber,
                                                                                                globalFilter ?? ''
                                                                                            )}
                                                                                        </td>
                                                                                        <td>
                                                                                            {highlightSearchTerm(
                                                                                                orderLine.ProductName,
                                                                                                globalFilter ?? ''
                                                                                            )}
                                                                                        </td>
                                                                                        <td>
                                                                                            {highlightSearchTerm(
                                                                                                mapItemStatusCodeToText(
                                                                                                    orderLine.Status,
                                                                                                    resources
                                                                                                ),
                                                                                                globalFilter ?? ''
                                                                                            )}
                                                                                        </td>
                                                                                        <td>
                                                                                            {orderLine.RequestedDate &&
                                                                                                formatDate(
                                                                                                    context,
                                                                                                    new Date(orderLine.RequestedDate)
                                                                                                )}
                                                                                        </td>
                                                                                        <td>
                                                                                            {orderLine.ScheduleLines.map(
                                                                                                (
                                                                                                    sLine: IOrderHistorySchedulLine,
                                                                                                    index: number
                                                                                                ) => {
                                                                                                    return (
                                                                                                        <span
                                                                                                            className='ms-order-history__block-item'
                                                                                                            data-cy='ms-order-history__confirmation-date'
                                                                                                            key={index}
                                                                                                        >
                                                                                                            {sLine.ConfirmedDate &&
                                                                                                                formatDate(
                                                                                                                    context,
                                                                                                                    new Date(
                                                                                                                        sLine.ConfirmedDate
                                                                                                                    )
                                                                                                                )}
                                                                                                        </span>
                                                                                                    );
                                                                                                }
                                                                                            )}
                                                                                        </td>
                                                                                        <td>
                                                                                            <span className='ms-order-history__block-item'>
                                                                                                {highlightSearchTerm(
                                                                                                    formatNumber(
                                                                                                        context,
                                                                                                        Number(orderLine.RequestedQty)
                                                                                                    ),
                                                                                                    globalFilter ?? ''
                                                                                                )}
                                                                                                {` ${orderLine.RequestedUnit}`}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td>
                                                                                            {orderLine.ScheduleLines.map(
                                                                                                (sLine: IOrderHistorySchedulLine) => {
                                                                                                    return (
                                                                                                        <span
                                                                                                            className='ms-order-history__block-item'
                                                                                                            key={index}
                                                                                                        >
                                                                                                            {highlightSearchTerm(
                                                                                                                formatNumber(
                                                                                                                    context,
                                                                                                                    Number(
                                                                                                                        sLine.ConfirmedQty
                                                                                                                    )
                                                                                                                ),
                                                                                                                globalFilter ?? ''
                                                                                                            )}
                                                                                                            {` ${sLine.ConfirmedUnit}`}
                                                                                                        </span>
                                                                                                    );
                                                                                                }
                                                                                            )}
                                                                                        </td>
                                                                                        <td>
                                                                                            {orderLine.Currency
                                                                                                ? formatCurrency(
                                                                                                      context,
                                                                                                      orderLine.Currency,
                                                                                                      Number(orderLine.NetValue)
                                                                                                  )
                                                                                                : NO_DATA_FALLBACK}
                                                                                        </td>
                                                                                    </tr>
                                                                                </React.Fragment>
                                                                            );
                                                                        }
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                            <a
                                                                className='ms-order-history__documents-link'
                                                                href={`${getOrderDetailsPageUrl(
                                                                    context.actionContext,
                                                                    Number(row.original.OrderNumber)
                                                                )}#${ORDER_DOCUMENTS_SECTION}`}
                                                            >
                                                                {resources.orderHistoryShowDocumentsLabel}
                                                            </a>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {totalEntries > 0 && totalEntries > pageSize && (
                        <div className='msc-pagination'>
                            <button
                                className='msc-pagination__button msc-pagination__button--is-previous'
                                onClick={() => setPageIndex(pageIndex - 1)}
                                disabled={!table.getCanPreviousPage()}
                            ></button>
                            <span className='msc-pagination__page-count'>
                                {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
                            </span>
                            <button
                                className='msc-pagination__button msc-pagination__button--is-next'
                                onClick={() => setPageIndex(pageIndex + 1)}
                                disabled={!table.getCanNextPage()}
                            ></button>
                        </div>
                    )}
                </div>
                <div className='col-12 col-lg-3 order-1 order-lg-2 pl-lg-0'>
                    <SearchBox globalFilter={globalFilter ?? ''} setGlobalFilter={setGlobalFilter} resources={resources} />
                    <FilterBox table={table} />
                </div>
            </div>
        </>
    );
}
